import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../../config';
import { 
  X, 
  Calendar as CalendarIcon, 
  ChevronLeft, 
  ChevronRight, 
  Plus, 
  Trash2, 
  Clock,
  CheckCircle2,
  AlertCircle,
  Wrench,
  Droplets,
  CreditCard
} from 'lucide-react';
import Swal from 'sweetalert2';

const VehicleAvailabilityModal = ({ vehicle, token, onClose }) => {
  const [availability, setAvailability] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  
  // Selection state
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  
  // Modal for adding/editing
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newReservation, setNewReservation] = useState({
    vehicle_id: vehicle.id,
    start_date: '',
    end_date: '',
    type: 'Kiralama',
    notes: ''
  });

  const typeOptions = ["Kiralama", "Rezerve", "Bakım", "Serviste", "Temizlikte", "Kapora Alındı"];

  const fetchAvailability = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/admin/availability/${vehicle.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAvailability(data);
      }
    } catch (err) {
      console.error('Availability fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [vehicle.id, token]);

  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  const handlePrevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  const handleNextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));

  const handleDayMouseDown = (dateStr) => {
    setSelectionStart(dateStr);
    setSelectionEnd(dateStr);
    setIsSelecting(true);
  };

  const handleDayMouseEnter = (dateStr) => {
    if (isSelecting) setSelectionEnd(dateStr);
  };

  const handleDayMouseUp = (dateStr) => {
    if (isSelecting && selectionStart) {
      const start = selectionStart < dateStr ? selectionStart : dateStr;
      const end = selectionStart < dateStr ? dateStr : selectionStart;
      
      setNewReservation({
        ...newReservation,
        start_date: `${start}T09:00`,
        end_date: `${end}T18:00`
      });
      setEditingId(null);
      setIsAddModalOpen(true);
    }
    setIsSelecting(false);
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    
    Swal.fire({
      title: 'Kaydediliyor...',
      background: '#0f172a',
      color: '#fff',
      didOpen: () => Swal.showLoading()
    });

    try {
      const url = editingId 
        ? `${API_URL}/admin/availability/${editingId}`
        : `${API_URL}/admin/availability`;
      
      const response = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ...newReservation, vehicle_id: vehicle.id })
      });
      
      if (response.ok) {
        fetchAvailability();
        setIsAddModalOpen(false);
        setEditingId(null);
        Swal.fire({ icon: 'success', title: 'Başarılı!', timer: 1500, showConfirmButton: false, background: '#0f172a', color: '#fff' });
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Hata!', text: 'Bir sorun oluştu.', background: '#0f172a', color: '#fff' });
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Emin misiniz?',
      text: "Bu takvim kaydını silmek istiyor musunuz?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      background: '#0f172a',
      color: '#fff'
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(`${API_URL}/admin/availability/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          fetchAvailability();
          Swal.fire({ icon: 'success', title: 'Silindi!', background: '#0f172a', color: '#fff' });
        }
      } catch (err) {
        console.error('Delete error:', err);
      }
    }
  };

  const daysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const days = daysInMonth(year, month);
    const startDay = (firstDayOfMonth(year, month) + 6) % 7; 
    
    const calendarDays = [];
    for (let i = 0; i < startDay; i++) {
      calendarDays.push(<div key={`prev-${i}`} style={{ height: '80px', border: '1px solid rgba(255,255,255,0.03)' }}></div>);
    }
    
    for (let day = 1; day <= days; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayAvailability = availability.filter(a => {
        const s = a.start_date.split('T')[0];
        const e = a.end_date.split('T')[0];
        return dateStr >= s && dateStr <= e;
      });

      const isToday = new Date().toISOString().split('T')[0] === dateStr;
      const isSelected = selectionStart && selectionEnd && 
        dateStr >= (selectionStart < selectionEnd ? selectionStart : selectionEnd) && 
        dateStr <= (selectionStart < selectionEnd ? selectionEnd : selectionStart);

      calendarDays.push(
        <div key={day} 
          onMouseDown={() => handleDayMouseDown(dateStr)}
          onMouseEnter={() => handleDayMouseEnter(dateStr)}
          onMouseUp={() => handleDayMouseUp(dateStr)}
          style={{ 
            height: '80px', 
            background: isSelected ? 'rgba(0, 209, 255, 0.15)' : 'rgba(255,255,255,0.02)', 
            border: isSelected ? '1px solid var(--primary)' : '1px solid rgba(255,255,255,0.05)',
            padding: '0.4rem',
            position: 'relative',
            cursor: 'cell',
            userSelect: 'none'
          }}>
          <span style={{ 
            fontSize: '0.7rem', 
            fontWeight: '800', 
            color: isToday ? 'var(--primary)' : 'var(--text-dim)',
            background: isToday ? 'rgba(0, 209, 255, 0.1)' : 'transparent',
            padding: '2px 4px',
            borderRadius: '4px'
          }}>{day}</span>
          
          <div style={{ marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {dayAvailability.map(a => (
              <div key={a.id} style={{ 
                fontSize: '0.55rem', 
                padding: '2px 4px', 
                borderRadius: '3px',
                background: a.type === 'Bakım' ? 'rgba(239, 68, 68, 0.2)' : a.type === 'Rezerve' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(34, 197, 94, 0.2)',
                color: a.type === 'Bakım' ? '#f87171' : a.type === 'Rezerve' ? '#fbbf24' : '#4ade80',
                borderLeft: `2px solid ${a.type === 'Bakım' ? '#ef4444' : a.type === 'Rezerve' ? '#f59e0b' : '#22c55e'}`,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
              }}>
                {a.type}
              </div>
            ))}
          </div>
        </div>
      );
    }
    return calendarDays;
  };

  const getEventIcon = (type) => {
    switch (type) {
      case 'Bakım': return <Wrench size={14} />;
      case 'Temizlikte': return <Droplets size={14} />;
      case 'Kapora Alındı': return <CreditCard size={14} />;
      case 'Kiralama': return <CheckCircle2 size={14} />;
      default: return <Clock size={14} />;
    }
  };

  return (
    <div className="availability-modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(2, 6, 23, 0.9)', backdropFilter: 'blur(10px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div className="availability-modal-content" style={{ background: '#0f172a', width: '100%', maxWidth: '1200px', maxHeight: '90vh', borderRadius: '2rem', border: '1px solid rgba(255,255,255,0.1)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        
        {/* Header */}
        <div style={{ padding: '1.5rem 2.5rem', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{ width: '44px', height: '44px', background: 'rgba(0, 209, 255, 0.1)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)' }}>
              <CalendarIcon size={24} />
            </div>
            <div>
              <h2 style={{ fontSize: '1.3rem', fontWeight: '900', color: 'white', textTransform: 'uppercase', letterSpacing: '1px' }}>ARAÇ MÜSAİTLİK DURUMU VE TAKVİM GEÇMİŞİ</h2>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>{vehicle.brand} {vehicle.model} — {vehicle.name}</p>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.05)', border: 'none', color: 'white', width: '40px', height: '40px', borderRadius: '10px', cursor: 'pointer' }}><X size={24} /></button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 350px', overflow: 'hidden' }}>
          
          {/* Left: Calendar */}
          <div style={{ padding: '2rem', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
               <h3 style={{ fontSize: '1rem', fontWeight: '800', color: 'white', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                 <CalendarIcon size={18} color="var(--primary)" /> MÜSAİTLİK TAKVİMİ
               </h3>
               <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', background: 'rgba(255,255,255,0.03)', padding: '0.4rem 1rem', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <button onClick={handlePrevMonth} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}><ChevronLeft size={18} /></button>
                  <span style={{ fontWeight: '800', fontSize: '0.9rem', minWidth: '120px', textAlign: 'center' }}>
                    {currentDate.toLocaleString('tr-TR', { month: 'long', year: 'numeric' })}
                  </span>
                  <button onClick={handleNextMonth} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}><ChevronRight size={18} /></button>
               </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '0', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', overflow: 'hidden' }}>
              {['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'].map(d => (
                <div key={d} style={{ padding: '0.6rem', textAlign: 'center', fontSize: '0.7rem', fontWeight: '900', color: '#64748b', background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>{d}</div>
              ))}
              {renderCalendar()}
            </div>

            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
              {[
                { l: 'KİRALAMA', c: '#22c55e' },
                { l: 'REZERVE', c: '#f59e0b' },
                { l: 'BAKIM', c: '#ef4444' }
              ].map(item => (
                <div key={item.l} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.65rem', fontWeight: '800', color: '#64748b' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '2px', background: item.c }}></div> {item.l}
                </div>
              ))}
            </div>
          </div>

          {/* Right: History List */}
          <div style={{ background: 'rgba(0,0,0,0.1)', borderLeft: '1px solid rgba(255,255,255,0.05)', padding: '2rem', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
               <h4 style={{ fontSize: '0.9rem', fontWeight: '800', color: 'white' }}>TAKVİM GEÇMİŞİ</h4>
               <button 
                 onClick={() => { setEditingId(null); setNewReservation({ vehicle_id: vehicle.id, start_date: '', end_date: '', type: 'Kiralama', notes: '' }); setIsAddModalOpen(true); }}
                 style={{ background: 'var(--primary)', border: 'none', width: '28px', height: '28px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
               ><Plus size={18} color="black" /></button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {availability.map(item => (
                <div key={item.id} className="history-item" style={{ background: 'rgba(255,255,255,0.02)', padding: '1rem', borderRadius: '1rem', border: '1px solid rgba(255,255,255,0.05)', position: 'relative' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{ padding: '4px', borderRadius: '6px', background: item.type === 'Bakım' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(34, 197, 94, 0.1)', color: item.type === 'Bakım' ? '#ef4444' : '#22c55e' }}>{getEventIcon(item.type)}</div>
                      <span style={{ fontSize: '0.75rem', fontWeight: '800', color: 'white', textTransform: 'uppercase' }}>{item.type}</span>
                    </div>
                    <button onClick={() => handleDelete(item.id)} style={{ background: 'none', border: 'none', color: '#ef4444', opacity: 0.5, cursor: 'pointer' }}><Trash2 size={14} /></button>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>
                    <strong>{new Date(item.start_date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}</strong> — <strong>{new Date(item.end_date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}</strong>
                  </div>
                  {item.notes && <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', fontStyle: 'italic', borderTop: '1px solid rgba(255,255,255,0.03)', paddingTop: '0.5rem', marginTop: '0.5rem' }}>{item.notes}</div>}
                </div>
              ))}
              {availability.length === 0 && <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-dim)', fontSize: '0.8rem' }}>Henüz kayıt yok.</div>}
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {isAddModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#1e293b', width: '400px', borderRadius: '1.5rem', padding: '2rem', border: '1px solid rgba(255,255,255,0.1)' }}>
            <h3 style={{ color: 'white', marginBottom: '1.5rem', fontSize: '1.1rem', fontWeight: '800' }}>{editingId ? 'Kaydı Düzenle' : 'Yeni Kayıt Ekle'}</h3>
            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ fontSize: '0.7rem', color: '#94a3b8', display: 'block', marginBottom: '0.4rem' }}>TÜR</label>
                <select 
                  value={newReservation.type} 
                  onChange={e => setNewReservation({...newReservation, type: e.target.value})}
                  style={{ width: '100%', background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', padding: '0.7rem', color: 'white' }}
                >
                   {typeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ fontSize: '0.7rem', color: '#94a3b8', display: 'block', marginBottom: '0.4rem' }}>BAŞLANGIÇ</label>
                  <input type="date" value={newReservation.start_date.split('T')[0]} onChange={e => setNewReservation({...newReservation, start_date: `${e.target.value}T09:00`})} style={{ width: '100%', background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', padding: '0.7rem', colorScheme: 'dark' }} />
                </div>
                <div>
                  <label style={{ fontSize: '0.7rem', color: '#94a3b8', display: 'block', marginBottom: '0.4rem' }}>BİTİŞ</label>
                  <input type="date" value={newReservation.end_date.split('T')[0]} onChange={e => setNewReservation({...newReservation, end_date: `${e.target.value}T18:00`})} style={{ width: '100%', background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', padding: '0.7rem', colorScheme: 'dark' }} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: '0.7rem', color: '#94a3b8', display: 'block', marginBottom: '0.4rem' }}>NOTLAR</label>
                <textarea value={newReservation.notes} onChange={e => setNewReservation({...newReservation, notes: e.target.value})} style={{ width: '100%', background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', padding: '0.7rem', color: 'white', minHeight: '60px' }} />
              </div>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <button type="button" onClick={() => setIsAddModalOpen(false)} style={{ flex: 1, padding: '0.8rem', borderRadius: '10px', border: 'none', background: 'rgba(255,255,255,0.05)', color: 'white', cursor: 'pointer' }}>İptal</button>
                <button type="submit" style={{ flex: 1, padding: '0.8rem', borderRadius: '10px', border: 'none', background: 'var(--primary)', color: 'black', fontWeight: '800', cursor: 'pointer' }}>{editingId ? 'Güncelle' : 'Kaydet'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .history-item { transition: all 0.2s; }
        .history-item:hover { transform: translateX(5px); background: rgba(255,255,255,0.04) !important; }
      `}</style>
    </div>
  );
};

export default VehicleAvailabilityModal;
